import { useEffect, type ReactNode } from "react";
import { RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/** Standard master-list page chrome (Materials, Clients, Vendors, Locations, Expenses). */
export function MasterPage({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("space-y-2", className)}>{children}</div>;
}

export function MasterHeader({
  title,
  icon,
  countLabel,
  actions,
  onRefresh,
  refreshing,
}: {
  title: ReactNode;
  icon?: ReactNode;
  countLabel?: ReactNode;
  actions?: ReactNode;
  onRefresh?: () => void;
  refreshing?: boolean;
}) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex min-w-0 items-baseline gap-2">
        <h1 className="flex items-center gap-1.5 text-base font-semibold tracking-[-0.01em]">
          {icon ? <span className="text-primary [&_svg]:size-4">{icon}</span> : null}
          {title}
        </h1>
        {countLabel ? (
          <span className="text-2xs tabular-nums text-muted-foreground">{countLabel}</span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {onRefresh ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Refresh"
            onClick={onRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={cn(refreshing && "animate-spin")} />
          </Button>
        ) : null}
        {actions}
      </div>
    </header>
  );
}

/** Compact KPI strip — prefer over large StatCards on masters. */
export function MasterSummaryStrip({
  items,
  className,
}: {
  items: { label: string; value: ReactNode; tone?: "default" | "warning" | "success" }[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-stretch gap-px overflow-hidden rounded-md border border-border bg-border",
        className,
      )}
    >
      {items.map((item) => (
        <div
          key={item.label}
          className={cn(
            "min-w-[7rem] flex-1 bg-card px-2.5 py-1.5",
            item.tone === "warning" && "bg-warning/10",
            item.tone === "success" && "bg-success/10",
          )}
        >
          <div className="text-[10px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
            {item.label}
          </div>
          <div
            className={cn(
              "text-sm font-semibold tabular-nums leading-tight",
              item.tone === "warning" && "text-warning",
              item.tone === "success" && "text-success",
            )}
          >
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Horizontal filter row — search first, filters inline, count on the right. */
export function MasterFilterBar({
  children,
  shownLabel,
  className,
}: {
  children: ReactNode;
  shownLabel?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {children}
      {shownLabel ? (
        <span className="ml-auto text-2xs tabular-nums text-muted-foreground">{shownLabel}</span>
      ) : null}
    </div>
  );
}

export function MasterSearchInput({
  id = "master-search",
  value,
  onChange,
  placeholder = "Search…  (/)",
  className,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={cn("relative", className)}>
      <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-52 pl-7 sm:w-64"
      />
    </div>
  );
}

export function MasterTabs<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="inline-flex h-7 items-center rounded-md border border-border bg-muted/50 p-0.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            "h-6 rounded px-2 text-2xs transition-colors",
            value === opt.value
              ? "bg-card font-medium text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function MasterBanner({
  children,
  tone = "muted",
}: {
  children: ReactNode;
  tone?: "muted" | "danger";
}) {
  return (
    <p
      className={cn(
        "rounded-md border px-2.5 py-1.5 text-2xs",
        tone === "danger"
          ? "border-destructive/30 bg-destructive/5 text-destructive"
          : "border-dashed border-border bg-muted/40 text-muted-foreground",
      )}
    >
      {children}
    </p>
  );
}

/**
 * `/` focuses search · `n` or Ctrl+N creates (when not typing).
 */
export function useMasterListShortcuts({
  searchInputId = "master-search",
  onCreate,
  enabled = true,
}: {
  searchInputId?: string;
  onCreate?: () => void;
  enabled?: boolean;
}) {
  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const typing =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.tagName === "SELECT" ||
        target?.isContentEditable;
      if (e.key === "/" && !typing) {
        e.preventDefault();
        document.getElementById(searchInputId)?.focus();
        return;
      }
      if (!onCreate || typing) return;
      if (e.key.toLowerCase() === "n" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        onCreate();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "n") {
        e.preventDefault();
        onCreate();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchInputId, onCreate, enabled]);
}
